import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import * as XLSX from "xlsx";
import { baseUrl, formatDate } from "../../comman/config";
import { MenuViewFilter } from "../../components/Filter/DateSessionItemFilter";
import Loader from "../../components/Loader";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/solid";
import NoRecordsFound from "../../components/NoRecordsFound";
import { isFilterActive, matchFilterText, patchFilter } from "../../components/Filter/columnFilter";
import { dateSessionLinkQuery } from "../../components/Filter/reportLink";
import ColumnFilterRow from "../../components/Filter/ColumnFilterRow";
import { ItemName, itemDisplayName, itemSearchText, itemExportName } from "../../components/ItemName";

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};
const dec = (v, d) => {
  const n = parseFloat(v);
  return isNaN(n) ? "-" : n.toFixed(d);
};
// #2: only Kgs (1), Readymade-Kgs (4) and Readymade-Nos (6) are shown.
const ALLOWED_METHODS = [1, 4, 6];

// Map a master_report row onto the Consumption Report columns. All values come from the Master
// Report; the letters below are its column references (MR-E, MR-M, ...).
// Only Actual values are carried -- the bracketed (STD) figures were dropped from RAS,
// FG / Person and RM to FG ratio, so nothing reads the Std counterparts any more.
const toRow = (r) => ({
  menu_item_id: r.menu_item_id,                            // grouping key for the per-item popup
  date: r.date,
  session_name: r.session_name,
  session_id: r.session,                                   // the id, for the Raw Report link
  item_name: r.item_name || "",                            // Gujarati -- what the Menu Item cell shows
  eng_name: r.eng_name || "",                              // English -- the hidden label the item filter searches
  method: r.methodName?.name ?? "-",                       // Method (shows "Kgs" for mirror rows)
  method_id: Number(r.methodName?.id ?? r.measurements),   // which Consumption Master row this item's figures belong to
  is_kgs_equivalent: !!r.is_kgs_equivalent,                // a generated Kgs mirror, not an entry of its own
  estimated_count: num(r.raw_material?.estimated_count),   // Count Planned  (MR-E)
  dish_issue_count: num(r.final_dish_count),               // Count Dish Issued (MR-I)
  ras_actual: num(r.raw_mat_per_100_person),               // RAS Actual (MR-M)
  fg_person_actual: num(r.fg_consumption_per_person),      // FG/Person Actual (MR-R), grams
  ratio_actual: num(r.current_rm_to_fg_ratio),             // RM to FG ratio Actual (MR-V)
  bowl_actual: num(r.no_of_rotis_in_1_bowl_icebox),        // 1 Bowl Qty Actual (MR-Y)
  rmbowl_actual: num(r.current_rm_to_bowl_ratio),          // RM to Bowl Ratio Actual (MR-AA)
  ppb_actual: num(r.avg_person_per_bowl),                  // Avg Persons Per Bowl Actual (MR-AC)
  density_live: num(r.avg_density),                        // Density Actual = Live density (MR-AE, Meal Master)
});

// The Consumption Report renders FG / Person in grams for the Kgs methods -- the Raw Report
// multiplies the kg/person figure by 1000 -- while Readymade-Nos is a count and is left as it is.
// Consumption Master stores the figure in the method's own unit, so grams go back to kg on the way
// in. Every other figure the popup copies (RAS, RM to FG ratio, 1 Bowl Qty) is already in the
// master's units.
const FG_PER_PERSON_IN_GRAMS = [1, 4]; // Kgs, Readymade-Kgs
const toMasterFgPerPerson = (grams, methodId) =>
  FG_PER_PERSON_IN_GRAMS.includes(Number(methodId)) ? grams / 1000 : grams;

// What "Update Consumption Master" copies: the popup's Avg for each of these fields, onto the
// named Consumption Master column. The two figures the master derives from them (RM to Bowl Ratio
// and Persons per Bowl) are recomputed server-side with the Consumption Master form's formulas.
const MASTER_FIELDS = [
  { field: "ras_actual",        d: 2, column: "raw_mat_per_100_person",     label: "RM per 100 Person" },
  { field: "fg_person_actual",  d: 2, column: "fg_consumption_per_person",  label: "FG per Person" },
  { field: "ratio_actual",      d: 3, column: "rm_to_fg_ratio",             label: "RM to FG Ratio" },
  { field: "bowl_actual",       d: 2, column: "one_bowl_qty",               label: "One Bowl QTY" },
];

// Columns shown in the per-item Avg/Min/Max popup, with the SAME decimals as the table.
const DETAIL_COLS = [
  { label: "RAS", field: "ras_actual", d: 2 },
  { label: "FG / Person (gms)", field: "fg_person_actual", d: 2 },
  { label: "RM to FG ratio", field: "ratio_actual", d: 3 },
  // Three decimals, the same as the Density column in the table -- the popup and the row it was
  // opened from must not print the same figure two ways.
  { label: "Density", field: "density_live", d: 3 },
  { label: "Avg. Persons Per Bowl", field: "ppb_actual", d: 0 },
  { label: "1 Bowl Qty", field: "bowl_actual", d: 2 },
  { label: "RM to Bowl Ratio", field: "rmbowl_actual", d: 3 },
];

// Copy to the clipboard, for the Avg column's click-to-copy. navigator.clipboard needs a secure
// context and this is served over plain http on the kitchen's own network, where it is undefined --
// hence the hidden-textarea fallback. execCommand('copy') is deprecated but is what works there.
const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // Permission refused or no secure context -- fall through and try the textarea.
  }
  try {
    const field = document.createElement("textarea");
    field.value = text;
    field.setAttribute("readonly", "");
    // Off-screen rather than hidden: a display:none field cannot be selected, and scrolling the
    // page to a focused input would move the popup under the user.
    field.style.position = "fixed";
    field.style.top = "0";
    field.style.opacity = "0";
    document.body.appendChild(field);
    field.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(field);
    return copied;
  } catch (e) {
    return false;
  }
};

// Average of a field over rows (NaN when empty -> dec() renders "-").
const average = (rowsArr, field) => {
  const vals = rowsArr.map((r) => r[field]).filter((v) => !isNaN(v));
  if (!vals.length) return NaN;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
};

// The row holding the Min / Max of a field (for the clickable popup cells). null when empty.
const extremeRow = (rowsArr, field, kind) => {
  const valid = rowsArr.filter((r) => !isNaN(r[field]));
  if (!valid.length) return null;
  return valid.reduce(
    (best, r) => (kind === "Min" ? (r[field] < best[field] ? r : best) : (r[field] > best[field] ? r : best)),
    valid[0]
  );
};

// Inline per-column filter descriptors: `text(r)` returns the column's displayed value, which the
// filter matches against. Order matches the table's leaf columns (after the checkbox).
const COLUMNS = [
  { key: "date", text: (r) => formatDate(r.date) },
  { key: "session", text: (r) => r.session_name ?? "" },
  // Searches the English name too -- it's rendered as a hidden label beside the Gujarati one,
  // so typing "Rotli" finds રોટલી. See components/ItemName.
  { key: "item", text: itemSearchText },
  { key: "method", text: (r) => r.method ?? "" },
  { key: "ras", text: (r) => dec(r.ras_actual, 2) },
  { key: "fgp", text: (r) => dec(r.fg_person_actual, 2) },
  { key: "ratio", text: (r) => dec(r.ratio_actual, 3) },
  { key: "bowl", text: (r) => dec(r.bowl_actual, 2) },
  { key: "rmbowl", text: (r) => dec(r.rmbowl_actual, 3) },
  { key: "ppb", text: (r) => dec(r.ppb_actual, 0) },
  { key: "density", text: (r) => dec(r.density_live, 3) },
  { key: "planned", text: (r) => dec(r.estimated_count, 0) },
  { key: "dish", text: (r) => dec(r.dish_issue_count, 0) },
];

// Toggleable columns for "Show Columns" (identity columns Date/Session/Menu Item are always shown).
// An entry with `children` is a group: its checkbox drives the children, and the children can then
// be unticked individually. Every other entry is a single column and toggles itself. Only Count has
// more than one column here, so it is the only group.
const SHOW_COLS = [
  { key: "method", label: "Method" },
  { key: "ras", label: "RAS" },
  { key: "fgp", label: "FG / Person" },
  { key: "ratio", label: "RM to FG ratio" },
  { key: "bowl", label: "1 Bowl Qty" },
  { key: "rmbowl", label: "RM to Bowl Ratio" },
  { key: "ppb", label: "Avg. Persons Per Bowl" },
  { key: "density", label: "Density" },
  { key: "count", label: "Count", children: [
      { key: "planned", label: "Planned" },
      { key: "dish", label: "Dish Issued" },
  ] },
];

// Every key that actually stands for a column -- a group contributes its children, not itself.
const LEAF_COL_KEYS = SHOW_COLS.flatMap((c) => (c.children ? c.children.map((ch) => ch.key) : [c.key]));

// Ticked by default in "Show Columns". The rest stay available but start hidden, so the table
// opens on the figures this report is actually read for.
// Listed in the order the table lays them out, so this reads as the opening shape of the report.
const DEFAULT_COLS = ["method", "ras", "fgp", "ratio", "density", "planned", "dish"];

// Inline per-column filters -- operator set and matching live in components/Filter/columnFilter,
// so every report offers the same ones. The column supplies the displayed text to match against.
const matchFilter = (c, r, f) => matchFilterText(c.text(r), f);

// Default reporting window: the last year, ending today.
const todayISO = () => new Date().toISOString().split("T")[0];
const oneYearAgoISO = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().split("T")[0];
};

function ConsumptionReport() {
  // Default reporting period is the last year -- the report is read for trends, and a
  // single day almost never has enough records for the Avg / Min / Max popup to mean anything.
  const [fromDate, setFromDate] = useState(oneYearAgoISO);
  const [toDate, setToDate] = useState(todayISO);
  const [session, setSession] = useState([]);
  const [item, setItem] = useState([]);
  // Events picked in the filter's multi-select. The filter carries a single from/to pair, so
  // several events collapse to earliest start -> latest end; sending the ids lets the API drop the
  // dates that fall in the gaps between them. [] = report on the whole range.
  const [events, setEvents] = useState([]);
  const [rows, setRows] = useState([]);
  const [checked, setChecked] = useState(new Set()); // per-row selection; all checked by default on load
  // "Update Consumption Master" overwrites master data every plan is costed from, so it is the
  // super admin's call alone. Superadmin is role 0 -- role 1 is the ordinary admin.
  const isSuperAdmin = Number(localStorage.getItem("userRole")) === 0;
  const [detailItem, setDetailItem] = useState(null); // menu item shown in the Avg/Min/Max popup
  const [savingMaster, setSavingMaster] = useState(false); // "Update Consumption Master" in flight
  const [highlightIndex, setHighlightIndex] = useState(null); // row highlighted from a Min/Max click
  const highlightRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [filters, setFilters] = useState({}); // per-column inline search text
  const [visibleCols, setVisibleCols] = useState(() => new Set(DEFAULT_COLS));
  // Which groups are expanded in the Show Columns dropdown. Collapsed by default, matching the
  // other two reports.
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const toggleOpenGroup = (key) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  const [showColsOpen, setShowColsOpen] = useState(false);
  const showColsRef = useRef(null);
  const [searchParams] = useSearchParams();

  // Only rows whose date falls inside one of these ranges are kept. Set when opened for a
  // recurring event, whose occurrences are separated by unrelated events on the calendar.
  const inRanges = (rowDate, ranges) => {
    if (!ranges || !ranges.length) return true;
    const d = String(rowDate ?? "").slice(0, 10);
    return ranges.some((r) => d >= r.from && d <= r.to);
  };

  const fetchData = async (f, t, s, i, ranges = [], ev = events) => {
    setLoading(true);
    try {
      const sessionParam = s && s.length ? JSON.stringify(s) : "undefined";
      const itemParam = i && i.length ? JSON.stringify(i) : "undefined";
      // itemFilter=and -> only date+session combos where ALL selected items were used together.
      const url =
        `${baseUrl}/reports/master_report?fromDate=${f ?? ""}&toDate=${t ?? ""}` +
        `&session=${encodeURIComponent(sessionParam)}&item=${encodeURIComponent(itemParam)}` +
        // Picked events narrow the query server-side, so the dates that fall between two selected
        // events never come back. `ranges` still does the same job for the deep-link entry point,
        // which passes occurrence spans rather than event ids.
        `&event=${encodeURIComponent(JSON.stringify(ev ?? []))}&itemFilter=and`;
      const response = await axios.get(url);
      // fromDate/toDate is only a loose envelope when ranges are given -- narrow to the
      // event occurrences here. The "all items used together" check the API applies is
      // per date+session, so dropping whole dates afterwards can't break it.
      const data = (response.data.data ?? [])
        .filter((r) => r.is_ticked)                                  // #1: New Master Report (ticked) rows only
        // #2: Kgs / Readymade-Kgs / Readymade-Nos — match the DISPLAYED method (methodName.id) so
        // ltr->Kgs and nos->Kgs mirror rows (which keep measurements 2/3) count as Kgs.
        .filter((r) => ALLOWED_METHODS.includes(Number(r.methodName?.id ?? r.measurements)))
        .filter((r) => inRanges(r.date, ranges));
      const mapped = data.map(toRow);
      setRows(mapped);
      setChecked(new Set(mapped.map((_, i) => i))); // default: all rows checked
      setFilters({});
      setHighlightIndex(null);
      setSubmitted(true);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load consumption report");
    } finally {
      setLoading(false);
    }
  };

  // Opened from the "Check RAS / Consumption" button on Production Estimation / Historical Data:
  // ?items are the checked meal items, ?fromDate&toDate the span to report on. ?ranges (optional,
  // "from:to,from:to") lists each occurrence of a recurring event so the gaps between them drop out.
  useEffect(() => {
    const f = searchParams.get("fromDate");
    const t = searchParams.get("toDate");
    const itemsParam = searchParams.get("items");
    const rangesParam = searchParams.get("ranges");
    const sessionId = searchParams.get("sessionId");
    const sessionName = searchParams.get("sessionName");
    if (f && t && itemsParam) {
      const itemList = itemsParam
        .split(",")
        .filter(Boolean)
        .map((v) => ({ value: Number(v) }));
      const ranges = (rangesParam ?? "")
        .split(",")
        .filter(Boolean)
        .map((r) => {
          const [from, to] = r.split(":");
          return { from, to };
        })
        .filter((r) => r.from && r.to);
      // Restrict to the selected session when one is passed; otherwise report on all sessions.
      const sessionList = sessionId
        ? [{ value: Number(sessionId), label: sessionName ?? String(sessionId) }]
        : [];
      setFromDate(f);
      setToDate(t);
      setItem(itemList);
      setSession(sessionList);
      fetchData(f, t, sessionList, itemList, ranges);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (f, t, s, i, event, immediate = false) => {
    // "All" (the sentinel option) means no narrowing, so it is dropped rather than sent.
    const evArr = (Array.isArray(event) ? event : []).filter((o) => o && o.value !== 'all');
    setEvents(evArr);
    // Keep session/item as arrays -- the render maps over them (session.map), so a stray
    // object (e.g. an "All" object) would crash the screen.
    const sArr = Array.isArray(s) ? s : [];
    const iArr = Array.isArray(i) ? i : [];
    setFromDate(f);
    setToDate(t);
    setSession(sArr);
    setItem(iArr);
    if (immediate) fetchData(f, t, sArr, iArr, [], evArr);
  };

  const handleSubmitFilters = () => fetchData(fromDate, toDate, session, item, [], events);

  const toggleRow = (index) =>
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  const allChecked = rows.length > 0 && checked.size === rows.length;
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(rows.map((_, i) => i)));

  // Rows for the popup: the CHECKED rows of the selected menu item (its dates/sessions).
  const detailRows = detailItem
    ? rows.filter((r, i) => r.menu_item_id === detailItem.menu_item_id && checked.has(i))
    : [];

  // Clicking a Min/Max value jumps to that row: highlight it and close the popup.
  const goToRow = (row) => {
    setHighlightIndex(rows.indexOf(row));
    setDetailItem(null);
  };

  // Clicking an Avg value copies it, so a figure can be carried to another screen without being
  // read off and retyped -- or selected by hand, which on a phone means fighting the popup.
  const copyAvg = async (value) => {
    if (value === "-") return;
    if (await copyToClipboard(value)) toast.success(`Copied ${value}`);
    else toast.error("Could not copy to the clipboard");
  };

  // The popup's Avg figures, rounded exactly as the Avg column prints them -- the button copies
  // what is on screen, not a longer figure behind it. null when a column has no average at all.
  const masterValues = () => {
    const values = {};
    for (const f of MASTER_FIELDS) {
      const avg = average(detailRows, f.field);
      if (isNaN(avg)) return null;
      values[f.column] = Number(avg.toFixed(f.d));
    }
    values.fg_consumption_per_person =
      toMasterFgPerPerson(values.fg_consumption_per_person, detailItem?.method_id);
    return values;
  };

  // Why the button is unavailable, or null when it can be pressed. A Kgs mirror row is generated
  // for an item planned only in litres or only in Nos -- its Consumption Master entry is kept in
  // that other unit, so there is no row here whose figures belong to it. The same goes for an item
  // reported under two methods at once: the popup averages both, and the two are not in one unit.
  const methodsInPopup = new Set(detailRows.map((r) => r.method_id));
  const masterBlockedReason = !detailItem
    ? "No item selected."
    : detailItem.is_kgs_equivalent
      ? "This is a converted Kgs row. Update the item's own method in Consumption Master."
      : methodsInPopup.size > 1
        ? "This item is reported under more than one method, so the averages mix units."
        : !masterValues()
          ? "There is no average to copy yet."
          : null;

  // Copy the four Avg figures onto the item's Consumption Master row. The master's RM to Bowl Ratio
  // and Persons per Bowl are recalculated from them server-side, with the Consumption Master form's
  // own formulas, so a row written from here reads the same as one saved from that form.
  const updateConsumptionMaster = async () => {
    const values = masterValues();
    if (!values || masterBlockedReason) return;
    const summary = MASTER_FIELDS.map((f) => `  ${f.label} = ${values[f.column]}`).join("\n");
    const ok = window.confirm(
      `Update Consumption Master for ${itemDisplayName(detailItem)} (${detailItem.method}) ` +
      `from the averages of ${detailRows.length} record${detailRows.length === 1 ? "" : "s"}?\n\n${summary}\n\n` +
      `RM to Bowl Ratio and Persons per Bowl are recalculated from these. Nothing else on the row changes.`
    );
    if (!ok) return;
    setSavingMaster(true);
    try {
      const response = await axios.post(`${baseUrl}/consumption_master/update_from_report`, {
        menu_item_id: detailItem.menu_item_id,
        method: detailItem.method_id,
        ...values,
      });
      if (response.data?.status) {
        toast.success("Consumption Master updated");
        setDetailItem(null);
      } else {
        toast.error(response.data?.msg ?? "Could not update Consumption Master");
      }
    } catch (error) {
      console.error(error);
      toast.error("Could not update Consumption Master");
    } finally {
      setSavingMaster(false);
    }
  };

  // Scroll the highlighted row into view when it changes.
  useEffect(() => {
    if (highlightIndex != null && highlightRef.current) {
      highlightRef.current.scrollIntoView({ block: "center" });
    }
  }, [highlightIndex]);

  // Close the "Show Columns" dropdown on outside click.
  useEffect(() => {
    const onClick = (e) => { if (showColsRef.current && !showColsRef.current.contains(e.target)) setShowColsOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Show Columns helpers. Identity columns (checkbox/Date/Session/Menu Item) are always shown;
  // "count" drives the Planned + Dish Issued pair together.
  const allColsVisible = LEAF_COL_KEYS.every((k) => visibleCols.has(k));
  // A group ticks when all its children are shown, and sits indeterminate while only some are.
  const groupState = (c) => {
    const kids = c.children ?? [];
    const shown = kids.filter((ch) => visibleCols.has(ch.key)).length;
    return { all: shown === kids.length, some: shown > 0 && shown < kids.length };
  };
  const toggleGroup = (c) =>
    setVisibleCols((prev) => {
      const next = new Set(prev);
      const { all } = groupState(c);
      c.children.forEach((ch) => (all ? next.delete(ch.key) : next.add(ch.key)));
      return next;
    });
  const toggleCol = (key) =>
    setVisibleCols((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  const toggleAllCols = (checked) => setVisibleCols(checked ? new Set(LEAF_COL_KEYS) : new Set());
  const colVisible = (colKey) => {
    if (["date", "session", "item"].includes(colKey)) return true;
    return visibleCols.has(colKey);
  };
  const visibleColCount =
    4 +
    (visibleCols.has("method") ? 1 : 0) +
    ["ras", "fgp", "ratio", "bowl", "rmbowl", "ppb", "density"].filter((k) => visibleCols.has(k)).length +
    (visibleCols.has("planned") ? 1 : 0) +
    (visibleCols.has("dish") ? 1 : 0);

  // Inline per-column filters. Declared after colVisible because only VISIBLE columns filter --
  // a filter left behind on a column you then hide would otherwise keep silently dropping rows.
  // The original row index is preserved for the checkbox / jump-highlight.
  const setColFilter = (key, patch) => setFilters((prev) => patchFilter(prev, key, patch));
  const activeFilterCols = COLUMNS.filter((c) => colVisible(c.key) && isFilterActive(filters[c.key]));
  const hasFilters = activeFilterCols.length > 0;
  const rowMatches = (r) => activeFilterCols.every((c) => matchFilter(c, r, filters[c.key]));
  const visibleRows = rows.map((r, i) => ({ r, i })).filter(({ r }) => rowMatches(r));

  const handleExport = () => {
    if (!rows.length) return toast.error("No data to export");
    const data = rows.map((r) => ({
      Date: formatDate(r.date),
      Session: r.session_name,
      "Menu Item": itemExportName(r),
      Method: r.method,
      "RAS Actual": dec(r.ras_actual, 2),
      "FG/Person gms Actual": dec(r.fg_person_actual, 2),
      "RM to FG ratio Actual": dec(r.ratio_actual, 3),
      "1 Bowl Qty Actual": dec(r.bowl_actual, 2),
      "RM to Bowl Ratio Actual": dec(r.rmbowl_actual, 3),
      "Avg Persons Per Bowl Actual": dec(r.ppb_actual, 0),
      "Density (Live) Actual": dec(r.density_live, 3),
      "Count Planned": r.estimated_count,
      "Count Dish Issued": r.dish_issue_count,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), "Consumption Report");
    XLSX.writeFile(wb, "consumption_report.xlsx");
  };

  const th = "px-3 py-2 border text-xs uppercase bg-gray-50";
  const td = "px-3 py-2 border text-right";

  return (
    <div className="">
      {loading && <Loader />}
      <div className="flex justify-between items-center py-4 px-6">
        <h1 className="text-lg sm:text-xl md:text-2xl font-bold">Consumption Report</h1>
        <div className="flex items-center gap-3">
          <div className="relative" ref={showColsRef}>
            <button
              onClick={() => setShowColsOpen((o) => !o)}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Show Columns
            </button>
            {showColsOpen && (
              <div className="absolute right-0 mt-1 w-56 max-h-80 overflow-auto bg-white border border-gray-300 rounded-md shadow-lg z-30 p-2">
                <label className="flex items-center gap-2 px-2 py-1 text-sm font-semibold text-gray-900 border-b border-gray-200 cursor-pointer">
                  <input type="checkbox" checked={allColsVisible} onChange={(e) => toggleAllCols(e.target.checked)} />
                  All
                </label>
                {SHOW_COLS.map((c) => {
                  if (!c.children) {
                    return (
                      <label key={c.key} className="flex items-center gap-2 px-2 py-1 text-sm text-gray-800 hover:bg-gray-100 cursor-pointer">
                        <input type="checkbox" checked={visibleCols.has(c.key)} onChange={() => toggleCol(c.key)} />
                        {c.label}
                      </label>
                    );
                  }
                  const { all, some } = groupState(c);
                  return (
                    <div key={c.key} className="py-1 border-t border-gray-100">
                      <div className="flex items-center gap-2 px-2 py-1 text-sm font-semibold text-gray-900 hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={all}
                          // indeterminate is a DOM property React cannot set through JSX.
                          ref={(el) => { if (el) el.indeterminate = some; }}
                          onChange={() => toggleGroup(c)} />
                        {/* The name expands the group; only the box changes what is shown. */}
                        <button type="button" onClick={() => toggleOpenGroup(c.key)}
                          className="flex-1 flex items-center justify-between text-left cursor-pointer">
                          <span>{c.label}</span>
                          {openGroups.has(c.key)
                            ? <ChevronUpIcon className="w-4 h-4 shrink-0 text-gray-500" />
                            : <ChevronDownIcon className="w-4 h-4 shrink-0 text-gray-500" />}
                        </button>
                      </div>
                      {openGroups.has(c.key) && c.children.map((ch) => (
                        <label key={ch.key} className="flex items-center gap-2 pl-8 pr-2 py-0.5 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer">
                          <input type="checkbox" checked={visibleCols.has(ch.key)} onChange={() => toggleCol(ch.key)} />
                          {ch.label}
                        </label>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <Link to="/">
            <button className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600">Back</button>
          </Link>
          <Toaster position="top-center" reverseOrder={false} />
        </div>
      </div>

      <div className="relative shadow-md sm:rounded-lg">
        {/* When opened from "Check RAS / Consumption", seed the filter so the applied date span
            and selected items show as selected by default (sessions default to "All"). */}
        <MenuViewFilter
          handleFilterChange={handleFilterChange}
          handleSubmitFilters={handleSubmitFilters}
          handleExport={handleExport}
          handleClearFilters={() => setFilters({})}
          showExportPdf={false}
          multiEvent
          initialFromDate={fromDate}
          initialToDate={toDate}
          initialItemValues={item.map((i) => i.value).join(",")}
          initialSessionValues={session.map((s) => s.value).join(",")}
        />

        {submitted && (
          <div className="p-4">
            {rows.length > 0 ? (
              <>
                {/* Vertical scroll with sticky header. */}
                <div className="overflow-auto max-h-[65vh] border rounded">
                <table className="w-full text-sm text-gray-600 border">
                  <thead className="text-gray-700 bg-gray-50 sticky top-0 z-20">
                    <tr>
                      <th rowSpan="2" className={th}>
                        <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                      </th>
                      <th rowSpan="2" className={th}>Date</th>
                      <th rowSpan="2" className={th}>Session</th>
                      <th rowSpan="2" className={th + " text-left"}>Menu Item</th>
                      {visibleCols.has("method") && <th rowSpan="2" className={th}>Method</th>}
                      {visibleCols.has("ras") && <th rowSpan="2" className={th}>RAS<br /><span className="normal-case">Actual</span></th>}
                      {visibleCols.has("fgp") && <th rowSpan="2" className={th}>FG / Person (gms)<br /><span className="normal-case">Actual</span></th>}
                      {visibleCols.has("ratio") && <th rowSpan="2" className={th}>RM to FG ratio<br /><span className="normal-case">Actual</span></th>}
                      {visibleCols.has("bowl") && <th rowSpan="2" className={th}>1 Bowl Qty<br /><span className="normal-case">Actual</span></th>}
                      {visibleCols.has("rmbowl") && <th rowSpan="2" className={th}>RM to Bowl Ratio<br /><span className="normal-case">Actual</span></th>}
                      {visibleCols.has("ppb") && <th rowSpan="2" className={th}>Avg. Persons Per Bowl<br /><span className="normal-case">Actual</span></th>}
                      {visibleCols.has("density") && <th rowSpan="2" className={th}>Density<br /><span className="normal-case">Live</span></th>}
                      {/* Count kept at the end (after Density) */}
                      {(visibleCols.has("planned") || visibleCols.has("dish")) && (
                        <th colSpan={(visibleCols.has("planned") ? 1 : 0) + (visibleCols.has("dish") ? 1 : 0)} className={th}>Count</th>
                      )}
                    </tr>
                    <tr>
                      {visibleCols.has("planned") && <th className={th}>Planned</th>}
                      {visibleCols.has("dish") && <th className={th}>Dish Issued</th>}
                    </tr>
                    {/* Inline per-column filter row (contains / = / ≤ / ≥ / between / not between).
                        The leading cell matches the row-number column the body renders. */}
                    <ColumnFilterRow
                      thClassName="px-1 py-1 border bg-gray-50 align-top"
                      cols={[
                        { key: "__rownum", filterable: false },
                        ...COLUMNS.filter((c) => colVisible(c.key)).map((c) => ({ key: c.key })),
                      ]}
                      filters={filters}
                      onChange={setColFilter} />
                  </thead>
                  <tbody>
                    {visibleRows.map(({ r, i: index }) => (
                        <tr
                          key={index}
                          ref={index === highlightIndex ? highlightRef : null}
                          className={`border-b ${index === highlightIndex ? "bg-yellow-200" : "bg-white hover:bg-gray-50"}`}
                        >
                          <td className="px-3 py-2 border text-center">
                            <input type="checkbox" checked={checked.has(index)} onChange={() => toggleRow(index)} />
                          </td>
                          {/* The Raw Report for this one day and session, in a new tab. A real
                              href rather than an onClick, so it opens the way any other link
                              does -- middle-click and ctrl-click included. A row that somehow
                              carries no date has nothing to open and stays plain text. */}
                          <td className={td}>
                            {r.date ? (
                              <a
                                href={`/reports/master${dateSessionLinkQuery(r.date, r.session_id)}`}
                                target="_blank"
                                rel="noreferrer"
                                title="Open the Raw Report for this date and session"
                                className="underline decoration-dotted underline-offset-2 text-blue-600 hover:text-blue-800"
                              >
                                {formatDate(r.date)}
                              </a>
                            ) : formatDate(r.date)}
                          </td>
                          <td className={td}>{r.session_name}</td>
                          <td className="px-3 py-2 border font-medium text-gray-900">
                            <div className="flex items-center justify-between gap-2">
                              {/* English name is present but not shown, so the item filter matches
                                  either script -- typing "Rotli" finds રોટલી. */}
                              <span><ItemName item={r} /></span>
                              <button
                                type="button"
                                onClick={() => setDetailItem(r)}
                                title="Avg / Min / Max for this item"
                                className="text-gray-400 hover:text-blue-600 shrink-0"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                              </button>
                            </div>
                          </td>
                          {visibleCols.has("method") && <td className={td}>{r.method}</td>}
                          {visibleCols.has("ras") && <td className={td}>{dec(r.ras_actual, 2)}</td>}
                          {visibleCols.has("fgp") && <td className={td}>{dec(r.fg_person_actual, 2)}</td>}
                          {visibleCols.has("ratio") && <td className={td}>{dec(r.ratio_actual, 3)}</td>}
                          {visibleCols.has("bowl") && <td className={td}>{dec(r.bowl_actual, 2)}</td>}
                          {visibleCols.has("rmbowl") && <td className={td}>{dec(r.rmbowl_actual, 3)}</td>}
                          {visibleCols.has("ppb") && <td className={td}>{dec(r.ppb_actual, 0)}</td>}
                          {visibleCols.has("density") && <td className={td}>{dec(r.density_live, 3)}</td>}
                          {visibleCols.has("planned") && <td className={td}>{dec(r.estimated_count, 0)}</td>}
                          {visibleCols.has("dish") && <td className={td}>{dec(r.dish_issue_count, 0)}</td>}
                        </tr>
                    ))}
                    {visibleRows.length === 0 && (
                      <tr>
                        <td className="px-3 py-4 text-center text-gray-500" colSpan={visibleColCount}>No rows match the filters.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
                <div className="p-3 text-sm text-gray-600">
                  All rows are ticked by default. Untick any to exclude it, then click the <span className="font-semibold">eye</span> icon on a row to see Avg / Min / Max over that menu item's ticked rows.
                  {hasFilters && (
                    <button onClick={() => setFilters({})} className="ml-2 text-blue-600 hover:underline">Clear filters</button>
                  )}
                </div>
              </>
            ) : (
              <NoRecordsFound />
            )}
          </div>
        )}
      </div>

      {/* Per-item Avg / Min / Max popup */}
      {detailItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setDetailItem(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[85vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start px-4 py-3 border-b">
              <div>
                <div className="font-bold text-gray-900">
                  {itemDisplayName(detailItem)}
                  {detailItem.method ? ` (${detailItem.method})` : ""}
                </div>
                <div className="text-xs text-gray-500">
                  Avg / Min / Max over {detailRows.length} record{detailRows.length === 1 ? "" : "s"} — click a Min/Max value to jump to that row
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDetailItem(null)}
                className="text-gray-500 hover:text-gray-800 text-2xl leading-none"
              >
                &times;
              </button>
            </div>
            <div className="p-4">
              <table className="w-full text-sm border">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 border text-left">Column</th>
                    <th className="px-3 py-2 border text-right">Avg</th>
                    <th className="px-3 py-2 border text-right">Min</th>
                    <th className="px-3 py-2 border text-right">Max</th>
                  </tr>
                </thead>
                <tbody>
                  {DETAIL_COLS.map((c) => {
                    const avg = dec(average(detailRows, c.field), c.d);
                    // "-" is the empty average; there is nothing to copy and no affordance to show.
                    const canCopy = avg !== "-";
                    const minRow = extremeRow(detailRows, c.field, "Min");
                    const maxRow = extremeRow(detailRows, c.field, "Max");
                    return (
                      <tr key={c.field} className="border-b">
                        <td className="px-3 py-1.5 border text-left text-gray-800">{c.label}</td>
                        {/* Dotted underline rather than the Min/Max cells' blue: those jump to a
                            row, this copies, and the two should not read as the same action. */}
                        <td
                          className={`px-3 py-1.5 border text-right${canCopy ? " underline decoration-dotted underline-offset-2 cursor-pointer" : ""}`}
                          title={canCopy ? `Copy ${avg}` : undefined}
                          onClick={() => copyAvg(avg)}
                        >
                          {avg}
                        </td>
                        <td
                          className="px-3 py-1.5 border text-right text-blue-600 cursor-pointer hover:underline"
                          title="Go to this row"
                          onClick={() => minRow && goToRow(minRow)}
                        >
                          {minRow ? dec(minRow[c.field], c.d) : "-"}
                        </td>
                        <td
                          className="px-3 py-1.5 border text-right text-blue-600 cursor-pointer hover:underline"
                          title="Go to this row"
                          onClick={() => maxRow && goToRow(maxRow)}
                        >
                          {maxRow ? dec(maxRow[c.field], c.d) : "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Copies the Avg column onto this item's Consumption Master entry. Super admin only. */}
            {isSuperAdmin && (
              <div className="flex flex-wrap items-center justify-end gap-3 px-4 py-3 border-t">
                {masterBlockedReason && (
                  <span className="text-xs text-gray-500 text-right">{masterBlockedReason}</span>
                )}
                <button
                  type="button"
                  onClick={updateConsumptionMaster}
                  disabled={!!masterBlockedReason || savingMaster}
                  title="Copy RAS, FG / Person, RM to FG ratio and 1 Bowl Qty from the Avg column onto this item's Consumption Master entry"
                  className="px-4 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {savingMaster ? "Updating..." : "Update Consumption Master"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ConsumptionReport;
